import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Sparkles, Check, ArrowRight, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

export default function Consultations() {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [consultations, setConsultations] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadTiers();
    loadConsultations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTiers = async () => {
    try {
      const { data } = await api.get('/consultation-tiers');
      setTiers(data);
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  const loadConsultations = async () => {
    try {
      const { data } = await api.get('/consultations');
      setConsultations(data);
    } catch (error) {
      console.error('Failed to load consultations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTier) {
      toast.error('Please select a consultation tier');
      return;
    }

    setLoading(true);
    try {
      const { data: consultation } = await api.post('/consultations', {
        tier: selectedTier.id,
        title,
        description
      });

      const { data: checkout } = await api.post('/payments/checkout', {
        lookup_key: selectedTier.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        consultation_data: { consultation_id: consultation.id }
      });

      window.location.href = checkout.checkout_url;
    } catch (error) {
      toast.error('Failed to create consultation request');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#78A18E';
      case 'pending':
        return '#E8C265';
      case 'pending_payment':
        return '#5B7C99';
      default:
        return '#595553';
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Personal Consultation</h1>
          </div>
          <p className="text-lg max-w-2xl mx-auto" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Get personalized, faith-based guidance tailored to your unique relationship situation
          </p>
        </div>

        {/* Scope-of-service disclaimer (shown when booking is active) */}
        {false && (
        <div className="max-w-4xl mx-auto mb-10 p-5 rounded-3xl flex items-start gap-3" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFFBF3 100%)', border: '1px solid #E8C26540' }} data-testid="consultation-disclaimer">
          <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: '#E8C26525' }}>
            <AlertCircle className="w-5 h-5" style={{ color: '#B38A2E' }} />
          </div>
          <div style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E', lineHeight: 1.7, fontSize: 14 }}>
            <p className="mb-1"><strong>What a consultation is — and isn't.</strong></p>
            <p>
              Consultations are <strong>faith-based guidance and pastoral encouragement</strong> — <strong>not licensed therapy, counseling, medical advice, or mental-health treatment</strong>. If you're in crisis, please contact <strong>988</strong> (Suicide &amp; Crisis Lifeline) or <strong>911</strong>, or reach a licensed professional. By booking you agree to our <Link to="/terms" style={{ color: '#B38A2E', fontWeight: 700, textDecoration: 'underline' }}>Terms of Service</Link>.
            </p>
          </div>
        </div>
        )}

        {/* Paused notice — replaces booking UI while consultation service is on hold */}
        <div className="max-w-3xl mx-auto p-8 rounded-3xl mb-16 text-center" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFFFFF 100%)', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #E8C26540' }} data-testid="consultation-paused">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#E8C26525' }}>
            <AlertCircle className="w-7 h-7" style={{ color: '#B38A2E' }} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            One-on-one consultations are paused for now
          </h2>
          <p className="mb-4 max-w-xl mx-auto" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>
            I'm finalizing my LLC and business setup so I can serve you with the level of care and structure you deserve. Consultations will return soon — until then, please explore the articles, feelings guide, and prayer wall. There's abundance here for the season you're in.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/" className="px-6 py-2.5 rounded-full text-sm font-semibold transition hover:-translate-y-0.5" style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif' }} data-testid="consultation-paused-explore">
              Explore Articles
            </Link>
            <Link to="/feelings" className="px-6 py-2.5 rounded-full text-sm font-semibold" style={{ backgroundColor: '#FDFBF7', color: '#595553', border: '1px solid #E6E2D8', fontFamily: 'Nunito, sans-serif' }} data-testid="consultation-paused-feelings">
              Feelings Guide
            </Link>
          </div>
        </div>

        {/* Booking UI paused — leave code in place so it's easy to reactivate later.
            To restore: change `{false && (` below back to `{true && (` or remove the wrapper. */}
        {false && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier, index) => (
            <div
              key={tier.id}
              onClick={() => setSelectedTier(tier)}
              className={`p-8 rounded-3xl transition-all duration-300 cursor-pointer hover:-translate-y-2 ${selectedTier?.id === tier.id ? 'ring-4' : ''}`}
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid',
                borderColor: selectedTier?.id === tier.id ? '#E57A44' : '#E6E2D8',
                boxShadow: index === 1 ? '0 20px 60px rgba(229, 122, 68, 0.15)' : '0 8px 30px rgba(0, 0, 0, 0.04)',
                ringColor: '#E57A44'
              }}
              data-testid={`tier-card-${tier.id}`}
            >
              {index === 1 && (
                <div className="inline-block px-4 py-1 rounded-full mb-4" style={{ backgroundColor: '#E8C265', color: '#2C2A29', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Most Popular
                </div>
              )}
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{tier.name}</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#E57A44' }}>${tier.price}</span>
              </div>
              <p className="mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{tier.description}</p>
              <div className="space-y-3">
                {tier.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#78A18E', strokeWidth: 2.5 }} />
                    <span style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontSize: '14px' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto p-8 rounded-3xl mb-16" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Request Your Consultation</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600 }}>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Brief title for your consultation"
                data-testid="consultation-title"
              />
            </div>
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600 }}>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={6}
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Describe your situation and what guidance you're seeking..."
                data-testid="consultation-description"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !selectedTier}
              className="w-full py-4 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600, fontSize: '16px' }}
              data-testid="consultation-submit"
            >
              <span>{loading ? 'Processing...' : `Continue to Payment $${selectedTier?.price || 0}`}</span>
              {!loading && <ArrowRight className="w-5 h-5" style={{ strokeWidth: 2 }} />}
            </button>
          </form>
        </div>
        </>
        )}

        {consultations.length > 0 && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Your Consultations</h2>
            <div className="space-y-4">
              {consultations.map((consultation) => (
                <Link
                  key={consultation.id}
                  to={`/consultation/${consultation.id}`}
                  className="block p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1"
                  style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}
                  data-testid={`consultation-item-${consultation.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{consultation.title}</h3>
                        <span
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: `${getStatusColor(consultation.status)}20`, color: getStatusColor(consultation.status), fontFamily: 'Nunito, sans-serif' }}
                        >
                          {consultation.status === 'pending_payment' ? 'Pending Payment' : consultation.status.charAt(0).toUpperCase() + consultation.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{consultation.description.slice(0, 150)}...</p>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        Tier: {consultation.tier} • {new Date(consultation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: '#E57A44', strokeWidth: 2 }} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
